import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ModalController, IonicModule, AlertController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ACTIONS,
  REASONS_MAP,
  EVENT_TYPES,
  Action,
  Reason,
  CareEventPayload,
} from '../care-event.types';
import { UiUtilityService } from 'src/app/core/services/ui-utility.service';
import { StorageEnum } from 'src/app/shared/enums/storage.enum';
import { StorageService } from 'src/app/core/services/storage.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { CareEventService } from '../../dashboard/services/care-event.service';

@Component({
  standalone: true,
  selector: 'app-record-care-event',
  templateUrl: './record-care-event.component.html',
  styleUrls: ['./record-care-event.component.scss'],
  imports: [IonicModule, CommonModule, ReactiveFormsModule],
})
export class RecordCareEventComponent implements OnInit, OnDestroy {
  @Input() residentId!: string;

  form!: FormGroup;
  private destroy$ = new Subject<void>();
  isSubmitting = false; // Flag to prevent multiple submissions

  actions = ACTIONS;
  reasonsMap = REASONS_MAP;
  eventTypes = EVENT_TYPES;

  constructor(
    private modalController: ModalController,
    private fb: FormBuilder,
    private alertController: AlertController,
    private uiUtilityService: UiUtilityService,
    private storageService: StorageService,
    private careEventService: CareEventService,
    private headerService: HeaderService
  ) {}

  private hasSelection(): boolean {
    return !!(
      this.form.get('action')?.value ||
      this.form.get('reason')?.value ||
      this.form.get('eventType')?.value
    );
  }

  ngOnInit() {
    this.form = this.fb.group({
      action: [null, Validators.required],
      reason: [null, Validators.required],
      eventType: [null],
      notes: [''],
    });

    this.form
      .get('action')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((action) => {
        const reasonControl = this.form.get('reason');
        const eventTypeControl = this.form.get('eventType');

        reasonControl?.reset();
        eventTypeControl?.reset();

        if (action === Action.Changed || action === Action.Checked) {
          reasonControl?.setValue(Reason.Wet);
        } else if (action === Action.Toileted) {
          reasonControl?.setValue(Reason.NoChange);
        }
      });

    this.form
      .get('reason')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.form.get('eventType')?.reset();
        this.updateEventTypeValidators();
      });
  }

  getReasons(): Array<{ label: string; value: Reason }> {
    const action = this.form.get('action')?.value as Action | undefined;
    return action ? this.reasonsMap[action] || [] : [];
  }

  isEventTypeRequired(): boolean {
    return (
      this.form.get('action')?.value === Action.Toileted &&
      this.form.get('reason')?.value === Reason.Change
    );
  }

  customCounterFormatter(inputLength: number, maxLength: number) {
    return `${maxLength - inputLength} characters remaining`;
  }

  private updateEventTypeValidators() {
    const eventTypeControl = this.form.get('eventType');
    if (this.isEventTypeRequired()) {
      eventTypeControl?.setValidators(Validators.required);
    } else {
      eventTypeControl?.clearValidators();
    }
    eventTypeControl?.updateValueAndValidity();
  }

  async submit() {
    
    // Prevent execution if already submitting or if form is invalid
    if (this.isSubmitting || !this.form.valid) {
      return;
    }

    this.isSubmitting = true; // Lock button immediately on click

    if (this.form.valid) {
      const userId = this.storageService.getLocalStorage(
        StorageEnum.loginData,
        true
      )?.userId;

      const payload: CareEventPayload = {
        action: this.form.get('action')?.value,
        reason: this.form.get('reason')?.value,
        eventType: this.form.get('eventType')?.value,
        notes: this.form.get('notes')?.value,
        performedBy: userId,
      };

      if (this.isEventTypeRequired()) {
      }
      this.saveCareEvent(payload);
    }
  }
saveCareEvent(data: any) {
    const selectedFacility = this.headerService.getCurrentFacility;

    this.careEventService
      .recordCareEvent(this.residentId, selectedFacility.timeZone, data)
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.modalController.dismiss(); // Close modal on success
            this.uiUtilityService.showToast(
              'Care event recorded successfully.',
              'success'
            );
          } else {
            this.isSubmitting = false; // Re-enable button if operation failed
            this.uiUtilityService.showAlert(
              'Failed to record care event.',
              'Error'
            );
          }
        },
        error: (err) => {
          this.isSubmitting = false; // Re-enable button on network/server error
          console.error('Error saving care event:', err);
          this.uiUtilityService.showAlert(
            'An error occurred while processing your request.',
            'Error'
          );
        }
      });
  }
  OLDsaveCareEvent(data: any) {
    const selectedFacility = this.headerService.getCurrentFacility;

    this.careEventService
      .recordCareEvent(this.residentId, selectedFacility.timeZone, data)
      .subscribe((res) => {
        // close the modal after submission
        this.modalController.dismiss();

        if (res.success) {
          this.uiUtilityService.showToast(
            'Care event recorded successfully.',
            'success'
          );
        } else {
          this.isSubmitting = false; // Re-enable button if operation failed
          this.uiUtilityService.showAlert(
            'Failed to record care event.',
            'Error'
          );
        }
      });
  }

  async cancel(needConfirmation = false) {
    if (!needConfirmation) {
      await this.modalController.dismiss();
      return;
    }

    if (!this.hasSelection()) {
      await this.modalController.dismiss();
      return;
    }

    const alert = await this.alertController.create({
      header: 'Discard event?',
      message:
        'Are you sure you want to close this window and discard this event?',
      buttons: [
        {
          text: 'No',
          role: 'cancel',
        },
        {
          text: 'Yes',
          role: 'confirm',
        },
      ],
    });

    await alert.present();
    const { role } = await alert.onDidDismiss();
    if (role === 'confirm') {
      await this.modalController.dismiss();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
